import React, { useState, useContext } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import {
  ExportHubAPI,
  ExportProduct,
  ExportProductTier,
  ExportJurisdiction,
} from 'api/ExportHubAPI';
import { TIER_OPTIONS, JURISDICTION_OPTIONS } from './productPresets';
import StyledSingleSelect from './StyledSingleSelect';
import {
  JurisdictionDetailsFields,
  JurDetails,
  jurisdictionDetailRows,
} from './jurisdictionFields';
import {
  checkProductCredits,
  type ProductCreditCheck,
} from 'utils/productCredits';
import ProductCreditsModal from 'pages/import/ProductCreditsModal';

interface Props {
  open: boolean;
  databaseId: string;
  onClose: () => void;
  onCreated: (product: ExportProduct) => void;
}

interface FormState {
  name: string;
  internal_sku: string;
  description: string;
  manufacturer: string;
  country_of_origin: string;
  tier: ExportProductTier;
  materials: string;
  intended_end_use: string;
  hardware_authorized: boolean;
  tech_data_authorized: boolean;
  quantity: string;
  unit_of_measure: string;
  jurisdiction: ExportJurisdiction;
  jurisdiction_details: JurDetails;
  hts_code: string;
  destinations: string[];
}

const EMPTY: FormState = {
  name: '',
  internal_sku: '',
  description: '',
  manufacturer: '',
  country_of_origin: '',
  tier: 'Component',
  materials: '',
  intended_end_use: '',
  hardware_authorized: false,
  tech_data_authorized: false,
  quantity: '',
  unit_of_measure: '',
  jurisdiction: 'EAR99',
  jurisdiction_details: {},
  hts_code: '',
  destinations: [],
};

const STEPS = [
  'Identification',
  'Composition & Use',
  'Jurisdiction',
  'Destinations',
  'Review',
];

const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (_value: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <button
    type='button'
    className={`eh-toggle ${checked ? 'on' : ''}`}
    onClick={() => onChange(!checked)}
    role='switch'
    aria-checked={checked}
  >
    <span className='eh-toggle-track'>
      <span className='eh-toggle-thumb' />
    </span>
    <span className='eh-toggle-label'>{label}</span>
  </button>
);

const AddProductModal: React.FC<Props> = ({
  open,
  databaseId,
  onClose,
  onCreated,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;
  const logAppAction = useLogAppAction();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [destInput, setDestInput] = useState('');
  const [saving, setSaving] = useState(false);
  // Set when the team is out of product credits; renders the upgrade prompt.
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );

  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));

  const reset = () => {
    setForm(EMPTY);
    setDestInput('');
    setStep(0);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const addDestination = () => {
    const v = destInput.trim();
    if (v && !form.destinations.includes(v)) {
      patch({ destinations: [...form.destinations, v] });
    }
    setDestInput('');
  };

  const removeDestination = (d: string) =>
    patch({ destinations: form.destinations.filter((x) => x !== d) });

  const canAdvance = step !== 0 || form.name.trim().length > 0;

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setStep(0);
      return;
    }
    if (!groupId) {
      enqueueSnackbar('No active team selected.', { variant: 'error' });
      return;
    }
    logAppAction('export_hub:add_product');
    setSaving(true);
    try {
      // Export Hub products draw on the same product-credit pool as the Import
      // Hub / Products DB, so capacity is checked before the row is created.
      const check = await checkProductCredits(groupId, 1);
      if (!check.ok) {
        setCreditBlock(check);
        return;
      }
      const quantityNum = form.quantity.trim()
        ? Number(form.quantity)
        : undefined;
      const product = await ExportHubAPI.createProduct(databaseId, {
        name: form.name.trim(),
        internal_sku: form.internal_sku.trim() || undefined,
        description: form.description.trim() || undefined,
        manufacturer: form.manufacturer.trim() || undefined,
        country_of_origin: form.country_of_origin.trim() || undefined,
        tier: form.tier,
        materials: form.materials.trim() || undefined,
        intended_end_use: form.intended_end_use.trim() || undefined,
        hardware_authorized: form.hardware_authorized,
        tech_data_authorized: form.tech_data_authorized,
        quantity:
          quantityNum !== undefined && Number.isFinite(quantityNum)
            ? Math.max(0, Math.floor(quantityNum))
            : undefined,
        unit_of_measure: form.unit_of_measure.trim() || undefined,
        jurisdiction: form.jurisdiction,
        jurisdiction_details: form.jurisdiction_details,
        hts_code: form.hts_code.trim() || undefined,
        intended_destination_countries: form.destinations,
        group_id: groupId,
      });
      enqueueSnackbar('Product added.', { variant: 'success' });
      reset();
      onCreated(product);
    } catch (err) {
      console.error('Failed to add product:', err);
      enqueueSnackbar('Failed to add product. Please try again.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} className='eh-modal-root'>
      <div className='eh-modal eh-modal-wide' role='dialog' aria-label='Add Product'>
        <div className='eh-modal-head'>
          <h2 className='eh-modal-title'>Add Product</h2>
          <button
            className='eh-modal-close'
            onClick={handleClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-stepper'>
          {STEPS.map((label, idx) => (
            <div
              key={label}
              className={`eh-step ${idx === step ? 'active' : ''} ${
                idx < step ? 'done' : ''
              }`}
            >
              <span className='eh-step-dot'>
                {idx < step ? <CheckIcon style={{ fontSize: 14 }} /> : idx + 1}
              </span>
              <span className='eh-step-label'>{label}</span>
            </div>
          ))}
        </div>

        <div className='eh-modal-body'>
          {step === 0 && (
            <>
              <label className='eh-field'>
                <span className='eh-field-label'>
                  Product Name <span className='eh-req'>*</span>
                </span>
                <input
                  className='eh-input'
                  value={form.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder='e.g. Inertial Measurement Unit'
                  maxLength={300}
                  autoFocus
                />
              </label>
              <div className='eh-field-row'>
                <label className='eh-field'>
                  <span className='eh-field-label'>Internal SKU</span>
                  <input
                    className='eh-input'
                    value={form.internal_sku}
                    onChange={(e) => patch({ internal_sku: e.target.value })}
                    maxLength={120}
                  />
                </label>
                <label className='eh-field'>
                  <span className='eh-field-label'>Manufacturer</span>
                  <input
                    className='eh-input'
                    value={form.manufacturer}
                    onChange={(e) => patch({ manufacturer: e.target.value })}
                    maxLength={200}
                  />
                </label>
              </div>
              <label className='eh-field'>
                <span className='eh-field-label'>Description</span>
                <textarea
                  className='eh-input eh-textarea'
                  value={form.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  rows={2}
                  maxLength={4000}
                />
              </label>
              <div className='eh-field-row'>
                <label className='eh-field'>
                  <span className='eh-field-label'>Country of Origin</span>
                  <input
                    className='eh-input'
                    value={form.country_of_origin}
                    onChange={(e) =>
                      patch({ country_of_origin: e.target.value })
                    }
                    placeholder='e.g. United States'
                    maxLength={64}
                  />
                </label>
                <div className='eh-field'>
                  <span className='eh-field-label'>Tier</span>
                  <div className='eh-seg'>
                    {TIER_OPTIONS.map((t) => (
                      <button
                        key={t}
                        type='button'
                        className={`eh-seg-btn ${form.tier === t ? 'active' : ''}`}
                        onClick={() => patch({ tier: t })}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <label className='eh-field'>
                <span className='eh-field-label'>Materials</span>
                <textarea
                  className='eh-input eh-textarea'
                  value={form.materials}
                  onChange={(e) => patch({ materials: e.target.value })}
                  rows={2}
                  maxLength={2000}
                />
              </label>
              <label className='eh-field'>
                <span className='eh-field-label'>Intended End Use</span>
                <textarea
                  className='eh-input eh-textarea'
                  value={form.intended_end_use}
                  onChange={(e) => patch({ intended_end_use: e.target.value })}
                  rows={2}
                  maxLength={2000}
                />
              </label>
              <div className='eh-field-row'>
                <label className='eh-field'>
                  <span className='eh-field-label'>Quantity</span>
                  <input
                    className='eh-input'
                    type='number'
                    min={0}
                    value={form.quantity}
                    onChange={(e) => patch({ quantity: e.target.value })}
                  />
                </label>
                <label className='eh-field'>
                  <span className='eh-field-label'>Unit of Measure</span>
                  <input
                    className='eh-input'
                    value={form.unit_of_measure}
                    onChange={(e) => patch({ unit_of_measure: e.target.value })}
                    placeholder='e.g. each, kg'
                    maxLength={40}
                  />
                </label>
              </div>
              <div className='eh-field'>
                <span className='eh-field-label'>Authorizations</span>
                <div className='eh-toggle-row'>
                  <Toggle
                    label='Hardware authorized'
                    checked={form.hardware_authorized}
                    onChange={(v) => patch({ hardware_authorized: v })}
                  />
                  <Toggle
                    label='Tech data authorized'
                    checked={form.tech_data_authorized}
                    onChange={(v) => patch({ tech_data_authorized: v })}
                  />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className='eh-field'>
                <span className='eh-field-label'>
                  Jurisdiction <span className='eh-req'>*</span>
                </span>
                <StyledSingleSelect
                  options={JURISDICTION_OPTIONS.map((j) => ({
                    value: j,
                    label: j,
                  }))}
                  value={form.jurisdiction}
                  onChange={(v) =>
                    // Detail fields differ per regime — reset on change.
                    patch({
                      jurisdiction: v as ExportJurisdiction,
                      jurisdiction_details: {},
                    })
                  }
                  inline
                />
              </div>

              <JurisdictionDetailsFields
                jurisdiction={form.jurisdiction}
                values={form.jurisdiction_details}
                onChange={(d) => patch({ jurisdiction_details: d })}
              />

              <label className='eh-field'>
                <span className='eh-field-label'>HTS Code</span>
                <input
                  className='eh-input'
                  value={form.hts_code}
                  onChange={(e) => patch({ hts_code: e.target.value })}
                  placeholder='e.g. 9014.20.8040'
                  maxLength={40}
                />
                <span className='eh-field-hint'>
                  ECCN / USML now live under the Jurisdiction fields above.
                </span>
              </label>
            </>
          )}

          {step === 3 && (
            <div className='eh-field'>
              <span className='eh-field-label'>
                Intended Destination Countries
              </span>
              <div className='eh-chip-input'>
                {form.destinations.map((d) => (
                  <span key={d} className='eh-chip'>
                    {d}
                    <button
                      type='button'
                      className='eh-chip-x'
                      onClick={() => removeDestination(d)}
                      aria-label={`Remove ${d}`}
                    >
                      <CloseIcon style={{ fontSize: 13 }} />
                    </button>
                  </span>
                ))}
                <input
                  className='eh-chip-field'
                  value={destInput}
                  onChange={(e) => setDestInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addDestination();
                    }
                  }}
                  onBlur={addDestination}
                  placeholder={
                    form.destinations.length ? 'Add another…' : 'Type a country, press Enter'
                  }
                />
              </div>
              <span className='eh-field-hint'>
                Effective destinations also include countries from linked
                customers/consignees (added later).
              </span>
            </div>
          )}

          {step === 4 && (
            <div className='eh-review'>
              <ReviewRow label='Name' value={form.name} />
              <ReviewRow label='SKU' value={form.internal_sku} />
              <ReviewRow label='Manufacturer' value={form.manufacturer} />
              <ReviewRow label='Country of Origin' value={form.country_of_origin} />
              <ReviewRow label='Tier' value={form.tier} />
              <ReviewRow label='Jurisdiction' value={form.jurisdiction} />
              {jurisdictionDetailRows(
                form.jurisdiction,
                form.jurisdiction_details,
              ).map((r) => (
                <ReviewRow key={r.label} label={r.label} value={r.value} />
              ))}
              <ReviewRow label='HTS Code' value={form.hts_code} />
              <ReviewRow
                label='Quantity'
                value={
                  form.quantity
                    ? `${form.quantity} ${form.unit_of_measure}`.trim()
                    : ''
                }
              />
              <ReviewRow
                label='Authorizations'
                value={[
                  form.hardware_authorized ? 'Hardware' : null,
                  form.tech_data_authorized ? 'Tech Data' : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
              />
              <ReviewRow
                label='Destinations'
                value={form.destinations.join(', ')}
              />
            </div>
          )}
        </div>

        <div className='eh-modal-foot'>
          {step > 0 ? (
            <button
              className='btn-outline'
              type='button'
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Back
            </button>
          ) : (
            <button
              className='btn-outline'
              type='button'
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              className='btn-primary'
              type='button'
              onClick={() => setStep((s) => s + 1)}
              disabled={!canAdvance}
              style={{ opacity: canAdvance ? 1 : 0.6 }}
            >
              Next
            </button>
          ) : (
            <button
              className='btn-primary'
              type='button'
              onClick={handleSubmit}
              disabled={saving || !form.name.trim()}
              style={{ opacity: saving || !form.name.trim() ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Add Product'}
            </button>
          )}
        </div>
      </div>
      </Modal>
      <ProductCreditsModal
        check={creditBlock}
        teamId={groupId || ''}
        onClose={() => setCreditBlock(null)}
      />
    </>
  );
};

const ReviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className='eh-review-row'>
    <span className='eh-review-label'>{label}</span>
    <span className='eh-review-value'>{value || '—'}</span>
  </div>
);

export default AddProductModal;
